import { datadogRum } from "@datadog/browser-rum";
import { motion } from "framer-motion";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useState } from "react";
import TagManager from "react-gtm-module";
import { useAdpSearch } from "utils/adp-long-run";
import { useOnfidoCheck } from "utils/creditCheckHooks/useOnfidoCheck";
import getEligibilityText, {
  CHECK_ELIGIBILITY,
  ELIGIBILITY_CONFIRMED,
  INIT_CHECK,
  INIT_VERIFICATION,
  VERIFIED_IDN,
  VERIFY_IDN,
} from "utils/getEligibilityText";
import { displayFullAddress, getDataFromSessionStorage } from "utils/helper";

import {
  Container,
  Box,
  Button,
} from "@/components/onboardingSections/container";
import styles from "@/styles/onboarding.module.scss";

export default function AppSummary({
  expensesData,
  addressData,
  personalData,
  changePanel,
  biometricVerification,
  setApplicationSubmitted,
}) {
  const router = useRouter();
  const [disabled, setDisabled] = useState(true);
  const [loading, setLoading] = useState(false);

  const [eligibilityButtonText, setEligibilityButtonText] =
    useState(CHECK_ELIGIBILITY);
  const [detailsConfirmed, setDetailsConfirmed] = useState(false);

  const Edit = ({ children, onClick }) => {
    return (
      <span
        onClick={onClick}
        className="text-onmoPrimary underline underline-offset-4 cursor-pointer"
      >
        {children}
      </span>
    );
  };

  function calcAge(dateString) {
    var birthday = +new Date(dateString);
    return ~~((Date.now() - birthday) / 31557600000);
  }

  const handleRejectionOrFail = useCallback(() => {
    const query = JSON.stringify({
      mobile_phone: personalData.mobileNumbers,
      email_address: personalData.emailAddresses,
      salutation: personalData.title,
      first_name: personalData.firstName,
      last_name: personalData.lastName,
      birth_date: personalData.dateOfBirth,
      ...addressData,
    });
    const tagManagerArgs = {
      gtmId: "GTM-PVX89V7G",
      dataLayer: {
        event: "organic-declined",
      },
    };
    TagManager.initialize(tagManagerArgs);
    router.push(
      `/credit-card/declined?data=${query}`,
      "/credit-card/declined?nd=y",
    );
  }, [
    addressData,
    personalData.dateOfBirth,
    personalData.emailAddresses,
    personalData.firstName,
    personalData.lastName,
    personalData.mobileNumbers,
    personalData.title,
  ]);

  const { setSearch, searchData, adpSearchCompletedSuccessfully } =
    useAdpSearch({
      category: "adp11",
      journey: "credit-card",
      setEligibilityButtonText,
      personalData,
      addressData,
      gtmId: "GTM-PVX89V7G",
      gtmEvent: "organic-declined",
    });

  const submitData = async e => {
    e.preventDefault();
    datadogRum.addAction("5.2-App-summary-submitted", {
      onboardingChannel: "Organic",
    });
    setEligibilityButtonText(INIT_CHECK);
    setDisabled(true);
    setLoading(true);
    setDetailsConfirmed(true);
    setApplicationSubmitted && setApplicationSubmitted(true);
    try {
      const body = {
        onmouuid: getDataFromSessionStorage("applicationTokens").uuid,
        mobile_phone: "+44" + personalData.mobileNumbers,
        email_address: personalData.emailAddresses,
        salutation: personalData.title,
        first_name: personalData.firstName,
        last_name: personalData.lastName,
        birth_date: personalData.dateOfBirth,
        nett_monthly_income: expensesData.nett_monthly_income,
        monthly_housing_costs: expensesData.monthly_housing_costs,
        employment: expensesData.employment,
        other_cost: expensesData.other_cost,
        pay_frequency: expensesData.pay_frequency,
        monthly_credit_commitments: expensesData.monthly_credit_commitments,
        address: [addressData],
        NEW_ADDRESS_FORMATION: true,
        address_source: "WEB",
        product_type: "CREDITCARD",
      };
      try {
        const bb = window.IGLOO.getBlackbox();
        if (bb.finished) {
          body.blackbox = bb.blackbox;
        }
      } catch (error) {
        console.log(error.message);
      }
      const response = await fetch("/api/account/check-kyc", {
        method: "POST",
        headers: {
          Authorization: `${
            getDataFromSessionStorage("applicationTokens").onmoAT
          }`,
        },
        body: JSON.stringify(body),
      });
      const res = await response.json();

      if (res.data.message === "Processing CAT11") {
        setSearch(true);
      } else {
        handleRejectionOrFail();
      }
    } catch (err) {
      router.push("/credit-card/error"); //CAT 11 API FAILED
    }
  };
  const userAccountData = {
    applicationId:
      getDataFromSessionStorage("applicationTokens") &&
      getDataFromSessionStorage("applicationTokens").uuid,
    customerCreditLimit: searchData?.limit_max,
    address: {
      postCode: addressData?.postcode ?? "",
    },
  };

  // onfido check
  const {
    userClearedOnfido,
    onfidoSubmissionComplete,
    onfidoSubmissionInProgress,
    triggerIDV,
    userIDVRequired,
  } = useOnfidoCheck({
    journey: "credit-card",
    personalData,
    userAccountData,
  });

  useEffect(() => {
    if (!detailsConfirmed) {
      setEligibilityButtonText(CHECK_ELIGIBILITY);
      triggerIDV(false);
      setDisabled(false);
      setLoading(false);
      setApplicationSubmitted && setApplicationSubmitted(false);
    }
  }, [detailsConfirmed]);

  //trigger IDV after search is completed
  useEffect(() => {
    if (!biometricVerification) return;
    if (!detailsConfirmed) return;

    // if onfido submission is in progress or already done, do not trigger onfido check
    if (onfidoSubmissionInProgress || onfidoSubmissionComplete) return;

    if (adpSearchCompletedSuccessfully) {
      triggerIDV(true);
    }
  }, [
    adpSearchCompletedSuccessfully,
    biometricVerification,
    detailsConfirmed,
    onfidoSubmissionComplete,
    onfidoSubmissionInProgress,
  ]);

  //Redirect to contract when biometric verification is not needed
  useEffect(() => {
    if (!detailsConfirmed) return;
    if (adpSearchCompletedSuccessfully && biometricVerification === false) {
      datadogRum.addAction("6.5-OBV-page-closed", {
        onboardingChannel: "Organic",
      });
      router.push("/credit-card/contract");
    }
  }, [
    adpSearchCompletedSuccessfully,
    biometricVerification,
    detailsConfirmed,
    router,
  ]);

  //Redirect to contract if onfido is skipped or completed successfully
  useEffect(() => {
    if (!detailsConfirmed) return;
    if (!userClearedOnfido) return;
    if (userClearedOnfido) {
      datadogRum.addAction("6.5-OBV-page-closed", {
        onboardingChannel: "Organic",
      });
      router.push("/credit-card/contract");
    }
  }, [detailsConfirmed, router, userClearedOnfido]);

  useEffect(() => {
    if (adpSearchCompletedSuccessfully) {
      setEligibilityButtonText(ELIGIBILITY_CONFIRMED);
    } else if (biometricVerification) {
      if (userClearedOnfido) {
        setEligibilityButtonText(VERIFIED_IDN);
      } else if (onfidoSubmissionInProgress) {
        setEligibilityButtonText(VERIFY_IDN);
      } else if (userIDVRequired) {
        setEligibilityButtonText(INIT_VERIFICATION);
      }
    }
  }, [
    adpSearchCompletedSuccessfully,
    biometricVerification,
    userClearedOnfido,
    onfidoSubmissionInProgress,
    userIDVRequired,
  ]);

  const displayAddress = useMemo(() => {
    return displayFullAddress(addressData);
  }, [addressData]);

  return (
    <motion.div initial={{ scale: 0.8 }} animate={{ scale: 1 }}>
      <Container onSubmit={submitData}>
        <p className={styles.topTitle}>We&apos;re ready to check</p>
        <p className={styles.checkSub}>
          Here&apos;s what you&apos;ve told us.{" "}
          <span className="text-onmoPrimary underline underline-offset-4">
            Tap
          </span>{" "}
          below to update anything that needs changing.
        </p>
        <Box gap={8} width="alt">
          <div className={styles.checkText}>
            <p>
              I&apos;m{" "}
              <Edit onClick={() => changePanel("panel1")}>
                {personalData.firstName} {personalData.lastName}
              </Edit>
              , my age is{" "}
              <Edit onClick={() => changePanel("panel1")}>
                {calcAge(personalData.dateOfBirth.replace(/-/g, "/"))}
              </Edit>{" "}
              and I live at{" "}
              <Edit onClick={() => changePanel("panel1")}>
                {displayAddress}
              </Edit>
              .
            </p>
            <p>
              I am{" "}
              <Edit onClick={() => changePanel("panel3")}>
                {expensesData?.employment?.toLowerCase()}
              </Edit>
              . After tax, I take home{" "}
              <Edit onClick={() => changePanel("panel3")}>
                £{expensesData?.nett_monthly_income} monthly
              </Edit>
              .
            </p>
            <p>
              Each month I spend{" "}
              <Edit onClick={() => changePanel("panel3")}>
                £{expensesData?.monthly_housing_costs}
              </Edit>{" "}
              on housing,{" "}
              <Edit onClick={() => changePanel("panel3")}>
                £{expensesData?.monthly_credit_commitments}
              </Edit>{" "}
              on credit repayments, and{" "}
              <Edit onClick={() => changePanel("panel3")}>
                £{expensesData?.other_cost}
              </Edit>{" "}
              on food, transport, bills, entertainment and other expenses.
            </p>
            {loading && (
              <>
                <p className="text-xl font-medium text-center mt-5">
                  We are processing your application.
                </p>
                <p>
                  Please don’t close this page as it will cancel your
                  application. This can take up to 3 minutes
                </p>
              </>
            )}
          </div>
          <div className="hidden md:block">
            <Button disabled={disabled} altLoading={loading}>
              {getEligibilityText(eligibilityButtonText)}
            </Button>
          </div>
        </Box>
        <div className="block md:hidden">
          <Button disabled={disabled} altLoading={loading}>
            {getEligibilityText(eligibilityButtonText)}
          </Button>
        </div>
      </Container>
    </motion.div>
  );
}
